import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface PermissionModule {
  name: string;
  selected: boolean;
  description: string;
}

const AddNewPermissionPage: React.FC = () => {
  const navigate = useNavigate();
  
  const [permissionName, setPermissionName] = useState('');
  const [permissionDescription, setPermissionDescription] = useState('');
  const [accessLevel, setAccessLevel] = useState<'read' | 'write' | 'full'>('read');
  
  const [modules, setModules] = useState<PermissionModule[]>([
    { name: 'User Management', selected: false, description: 'Access to manage platform users' },
    { name: 'Campaign Management', selected: false, description: 'Access to create and manage SMS campaigns' },
    { name: 'Template Management', selected: false, description: 'Access to manage message templates' },
    { name: 'Billing & Finance', selected: false, description: 'Access to billing and financial features' },
    { name: 'Reports & Analytics', selected: false, description: 'Access to reporting and analytics tools' },
    { name: 'System Settings', selected: false, description: 'Access to platform configuration' },
  ]);

  const handleModuleToggle = (index: number) => {
    const updatedModules = [...modules];
    updatedModules[index].selected = !updatedModules[index].selected;
    setModules(updatedModules);
  };

  const handleSave = () => {
    // Implement save functionality
    console.log('Saving permission...', {
      name: permissionName,
      description: permissionDescription,
      accessLevel,
      modules: modules.filter(m => m.selected),
    });
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-medium">Add New Permission</h1>
        <div className="space-x-3">
          <button
            onClick={() => navigate('/superadmin/users-roles')}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#FF5722]/90 transition-colors"
          >
            Save Permission
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
        {/* Basic Information */}
        <div>
          <h2 className="text-lg font-medium mb-4">Permission Information</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Permission Name
              </label>
              <input
                type="text"
                value={permissionName}
                onChange={(e) => setPermissionName(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="e.g., Campaign Manager"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={permissionDescription}
                onChange={(e) => setPermissionDescription(e.target.value)}
                rows={3}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="Describe the permission's scope and purpose..."
              />
            </div>
          </div>
        </div>

        {/* Access Level */}
        <div>
          <h2 className="text-lg font-medium mb-4">Access Level</h2>
          <div className="space-x-4">
            <label className="inline-flex items-center">
              <input
                type="radio"
                checked={accessLevel === 'read'}
                onChange={() => setAccessLevel('read')}
                className="h-4 w-4 text-[#FF5722] border-gray-300 focus:ring-[#FF5722]"
              />
              <span className="ml-2">Read Only</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                checked={accessLevel === 'write'}
                onChange={() => setAccessLevel('write')}
                className="h-4 w-4 text-[#FF5722] border-gray-300 focus:ring-[#FF5722]"
              />
              <span className="ml-2">Write</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                checked={accessLevel === 'full'}
                onChange={() => setAccessLevel('full')}
                className="h-4 w-4 text-[#FF5722] border-gray-300 focus:ring-[#FF5722]"
              />
              <span className="ml-2">Full Access</span>
            </label>
          </div>
        </div>

        {/* Module Access */}
        <div>
          <h2 className="text-lg font-medium mb-4">Module Access</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {modules.map((module, index) => (
              <div 
                key={module.name}
                className="border rounded-lg p-4 hover:bg-gray-50"
              >
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    checked={module.selected}
                    onChange={() => handleModuleToggle(index)}
                    className="mt-1 h-4 w-4 text-[#FF5722] rounded border-gray-300 focus:ring-[#FF5722]"
                  />
                  <div>
                    <label className="block text-sm font-medium text-gray-900">
                      {module.name}
                    </label>
                    <p className="text-sm text-gray-500">{module.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddNewPermissionPage; 